import { useEffect, useState } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { RESTAURANT, ASSETS } from "@/data/restaurant";
import { useCart } from "@/context/CartContext";
import InstallAppButton from "@/components/InstallAppButton";

const NAV = [
  { href: "#menu", label: "Menu" },
  { href: "#gallery", label: "Gallery" },
  { href: "#drinks", label: "Drinks" },
  { href: "#reserve", label: "Reserve" },
  { href: "#location", label: "Visit" },
  { href: "#contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totals, openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-[#F9F6F0]/85 border-b border-[#E8E2D6]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-2"
        >
          <span className="inline-block w-10 h-10 rounded-full overflow-hidden border-2 border-[#F2B441] bg-white shadow-sm">
            <img
              src={ASSETS.logo}
              alt="Meritz logo"
              data-testid="header-logo"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 32%", transform: "scale(1.55)" }}
            />
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight">
            Meritz
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-[#2C2825]/80 hover:text-[#D9534F] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <InstallAppButton variant="header" />
          <button
            type="button"
            onClick={openCart}
            data-testid="header-cart-button"
            className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E8E2D6] text-[#2C2825] text-sm font-semibold hover:border-[#C73E3A] hover:text-[#C73E3A] transition-colors"
          >
            <ShoppingBag size={16} />
            <span>Cart</span>
            {totals.count > 0 && (
              <span
                data-testid="header-cart-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#C73E3A] text-white text-[10px] font-bold grid place-items-center"
              >
                {totals.count}
              </span>
            )}
          </button>
          <a
            href="#reserve"
            data-testid="cta-reserve-header"
            className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#C73E3A] text-white text-sm font-semibold hover:bg-[#2C2825] transition-colors shadow-md shadow-[#C73E3A]/20"
          >
            Reserve a Table
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <button
            type="button"
            onClick={openCart}
            data-testid="header-cart-button-mobile"
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 border border-[#E8E2D6]"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} />
            {totals.count > 0 && (
              <span
                data-testid="header-cart-badge-mobile"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C73E3A] text-white text-[10px] font-bold grid place-items-center"
              >
                {totals.count}
              </span>
            )}
          </button>
          <button
            type="button"
            aria-label="Toggle navigation"
            data-testid="nav-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/70 border border-[#E8E2D6]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="md:hidden bg-[#F9F6F0]/95 backdrop-blur-md border-b border-[#E8E2D6]"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="py-3 px-3 rounded-lg text-[#2C2825] font-medium hover:bg-[#E8E2D6]/60"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#reserve"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-reserve"
              className="mt-2 text-center bg-[#D9534F] text-white py-3 rounded-full font-semibold"
            >
              Reserve a Table
            </a>
            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              data-testid="mobile-call"
              className="text-center py-3 rounded-full font-semibold border border-[#2C2825] text-[#2C2825]"
            >
              Call {RESTAURANT.phoneDisplay}
            </a>
            <InstallAppButton variant="mobile" />
          </div>
        </div>
      )}
    </header>
  );
}
